import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridColumns,
  GridPinnedColumns,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { MagicCheckbox } from 'pages/Shipments/components/ContainerActions/MagicCheckbox';
import { useContainerSelection } from 'common/hooks/container-selection';
import { SHIPMENTS_QUERY_PARAMS } from 'pages/Shipments/params';
import * as React from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { WatchList } from 'services/clients/watchList';
import { brandColors } from 'brandColors';
import { SelectionToolbar } from 'components/ContainerActions/SelectionToolbar';
import { NoneDeletedOverlay } from 'components/ContainerTable/NoneDeletedOverlay';
import { useHasUserFilters } from '../utilities';
import { AddShipmentsOverlay, NoShipmentsFoundOverlay } from './overlays';

interface Props {
  shipmentsQuery: UseQueryResult<WatchList>;
  columns: GridColumns;
  columnVisibilityModel?: GridColumnVisibilityModel;
  pinnedColumns?: GridPinnedColumns;
  setShowTooltip?: (field: string) => void;
  showTooltip?: boolean;
  variant: 'active' | 'deleted';
}

export const Table: React.FC<Props> = (props) => {
  const {
    shipmentsQuery,
    columns,
    columnVisibilityModel,
    pinnedColumns,
    setShowTooltip,
    showTooltip,
    variant,
  } = props;
  const { searchParams, setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);
  const { handleOnSelectionModelChange, selectionModel } = useContainerSelection(
    (c) => `${c.transaction}_${c.containerNumber}`,
    shipmentsQuery.data
  );
  const rows = shipmentsQuery.data?.results || [];
  const dataGridApiRef = useGridApiRef();

  const hasFilters = useHasUserFilters(true);

  let NoResultsOrRowsOverlay = AddShipmentsOverlay;
  if (variant === 'deleted') {
    NoResultsOrRowsOverlay = NoneDeletedOverlay;
  } else if (hasFilters || searchParams.containerNumbers.length > 0) {
    NoResultsOrRowsOverlay = NoShipmentsFoundOverlay;
  }

  return (
    <DataGridPro
      apiRef={dataGridApiRef}
      rows={rows}
      columnVisibilityModel={columnVisibilityModel}
      columns={columns}
      rowCount={shipmentsQuery.data?.count || 0}
      getRowId={(r) => `${r.transaction}_${r.containerNumber}`}
      pagination
      getRowClassName={(params) =>
        params.row.unwatchedAt && 'bg-surface-interactive-disabled-accent-2'
      }
      paginationMode="server"
      pinnedColumns={pinnedColumns}
      page={searchParams.page - 1}
      onPageChange={(page) => setSearchParams({ page: page + 1 })}
      pageSize={searchParams.pageSize}
      onPageSizeChange={(pageSize) => setSearchParams({ pageSize })}
      checkboxSelection
      loading={shipmentsQuery.isLoading}
      error={shipmentsQuery.error}
      onSelectionModelChange={handleOnSelectionModelChange}
      selectionModel={selectionModel}
      headerHeight={48}
      components={{
        NoRowsOverlay: NoResultsOrRowsOverlay,
        BaseCheckbox: MagicCheckbox,
        Toolbar: selectionModel.length > 0 ? SelectionToolbar : null,
      }}
      onColumnHeaderOver={(params) => {
        setShowTooltip?.(params.field);
      }}
      onColumnHeaderOut={() => {
        setShowTooltip?.('');
      }}
      classes={{
        cellCheckbox: 'flex items-center justify-center',
        columnHeaders: 'bg-white shadow-sm border-none z-10',
        columnHeader: 'bg-white',
        columnHeaderTitle: 'px-2 py-4',
        columnSeparator: `${showTooltip ? '' : 'hidden'}`,
        cell: 'flex items-start flex-col',
        root: 'bg-white border-none',
        row: 'border-b border-gray-300',
        main: variant === 'active' ? 'bg-white' : 'bg-surface-interactive-disabled-accent-2',
        footerContainer: 'flex flex-row-reverse flex-grow border-none bg-white',
        virtualScroller: `${selectionModel.length === 0 ? '' : 'pt-8'} scrollbar`,
      }}
      sx={{
        '.MuiDataGrid-cellCheckbox': {
          backgroundColor: 'transparent',
        },
        '.MuiDataGrid-cell': {
          borderBottom: 'none',
        },
        '.MuiDataGrid-cell:nth-of-type(1)': {
          borderRight: 'none',
        },
        '.MuiDataGrid-columnHeader:nth-of-type(1)': {
          borderRight: 'none',
        },
        '.MuiDataGrid-checkboxInput.Mui-checked': {
          color: `${brandColors.colors.blue.DEFAULT} !important`,
        },
        '.MuiDataGrid-checkboxInput': {
          color: `${brandColors.colors.gray[500]} !important`,
        },
        '.MuiDataGrid-row.Mui-selected': {
          background: `${brandColors.colors.gray[1000]}`,
        },
      }}
      disableColumnReorder
      disableColumnFilter
      disableColumnSelector
      disableChildrenSorting
      disableColumnMenu
      sortingMode="server"
      disableSelectionOnClick
    />
  );
};
Table.displayName = 'Shipments.Containers.Table';
